/**
 * V2TopBar — the V2 shell top bar.
 *
 * Mirrors design_reference/js/shell.jsx TopBar: page title, project-context
 * strip, global search, AppearancePopover, notifications bell. Title and
 * project strip read from the router + stores; the search binds to the
 * project store on the dashboard route only.
 */

import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Check, Menu } from 'lucide-react';

import type { Project } from '../../types';
import useProjectStore from '../../stores/projectStore';
import { cn, getProjectDisplayName } from '../../lib/utils';
import { Button } from '../components/ui/button';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '../components/ui/dropdown-menu';
import { Input } from '../components/ui/input';
import { Kbd } from '../components/ui/kbd';
import { AppearancePopover } from '../components/AppearancePopover';
import { IconBell, IconBuilding, IconSearch } from '../components/icons';

/**
 * Derives a page title from the current pathname.
 * Mirrors classic Header.tsx:25-36 plus the V2-specific routes.
 *
 * @param pathname - The current router pathname.
 * @returns The human-friendly page title.
 */
function getPageTitle(pathname: string): string {
  if (pathname === '/') return 'Dashboard';
  if (pathname.startsWith('/project/')) return 'Estimate';
  if (pathname.startsWith('/completeness')) return 'Supplement';
  if (pathname.startsWith('/comparisons')) return 'Comparisons';
  if (pathname.startsWith('/scope/builder')) return 'Scope Builder';
  if (pathname.startsWith('/chat')) return 'AI Chat';
  if (pathname.startsWith('/assistant')) return 'AI Assistant';
  if (pathname.startsWith('/macros')) return 'Macros';
  if (pathname.startsWith('/welcome')) return 'Welcome';
  if (pathname.startsWith('/admin/rules')) return 'Rule Library';
  if (pathname.startsWith('/admin')) return 'Admin';
  if (pathname.startsWith('/profile')) return 'Settings';
  if (pathname.startsWith('/v2/design')) return 'Design System';
  return 'AI Estimate Assistant';
}

/**
 * In-place project switcher. Replaces the old "Select"/"Switch" links that
 * dumped the user to the Dashboard. Opens an anchored dropdown of the user's
 * projects (Netlify-style); picking one sets it as the active project WITHOUT
 * leaving the current tool, and keeps a "View all projects" escape hatch.
 *
 * @param triggerLabel - Text for the trigger button ("Switch" or "Select").
 * @returns The switcher dropdown element.
 */
function ProjectSwitcherMenu({ triggerLabel }: { triggerLabel: string }) {
  const navigate = useNavigate();
  const location = useLocation();
  const projects = useProjectStore((s) => s.projects);
  const selectedProject = useProjectStore((s) => s.selectedProject);
  const setSelectedProject = useProjectStore((s) => s.setSelectedProject);
  const fetchProjects = useProjectStore((s) => s.fetchProjects);
  const loading = useProjectStore((s) => s.loading);

  // Reason: a user can deep-link straight into Scope Builder without ever
  // visiting the Dashboard, so the project list may be empty — fetch it the
  // first time the dropdown opens.
  const handleOpenChange = (open: boolean) => {
    if (open && projects.length === 0) void fetchProjects();
  };

  // Switch the active project in place. Tool pages (Scope Builder, Supplement)
  // read `selectedProject` from the store, so stripping any `?project=` query
  // makes them pick up the freshly-selected one; the estimate route is keyed by
  // path param, so navigate there explicitly.
  const handleSelect = (p: Project) => {
    setSelectedProject(p);
    if (location.pathname.startsWith('/project/')) {
      navigate(`/project/${p.id}`);
    } else {
      navigate(location.pathname);
    }
  };

  return (
    <DropdownMenu onOpenChange={handleOpenChange}>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className="h-6">
          {triggerLabel}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-[300px]">
        <DropdownMenuLabel>Switch project</DropdownMenuLabel>
        <div className="max-h-[320px] overflow-y-auto">
          {loading && projects.length === 0 ? (
            <div className="px-2 py-3 text-[12px] text-[var(--tx-lo)]">Loading projects…</div>
          ) : projects.length === 0 ? (
            <div className="px-2 py-3 text-[12px] text-[var(--tx-lo)]">No projects yet</div>
          ) : (
            projects.map((p) => {
              const isActive = p.id === selectedProject?.id;
              return (
                <DropdownMenuItem
                  key={p.id}
                  onSelect={() => handleSelect(p)}
                  className="h-auto flex-col items-start gap-0 py-1.5"
                >
                  <span className="flex w-full items-center gap-2">
                    <span className="flex-1 truncate font-[530] text-[var(--tx-hi)]">
                      {getProjectDisplayName(p)}
                    </span>
                    {isActive && (
                      <Check className="h-[13px] w-[13px] flex-none text-[var(--accent-ink)]" />
                    )}
                  </span>
                  <span className="w-full truncate text-[11px] text-[var(--tx-lo)]">
                    {p.street || p.address || '—'}
                  </span>
                </DropdownMenuItem>
              );
            })
          )}
        </div>
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link to="/" className="text-[var(--accent-ink)]">
            View all projects →
          </Link>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/**
 * Renders the V2 top bar.
 *
 * @param props - `onMenu` opens the nav drawer at phone width (B-7305).
 * @returns The top bar element.
 */
export function V2TopBar({
  onMenu,
  menuOpen = false,
}: { onMenu?: () => void; menuOpen?: boolean } = {}) {
  const location = useLocation();
  const selectedProject = useProjectStore((s) => s.selectedProject);
  const searchQuery = useProjectStore((s) => s.searchQuery);
  const setSearchQuery = useProjectStore((s) => s.setSearchQuery);

  const title = getPageTitle(location.pathname);
  const isDashboard = location.pathname === '/';

  return (
    <header className="v2-topbar flex h-[var(--topbar-h)] flex-none items-center gap-[14px] border-b border-[var(--bd-subtle)] bg-[color-mix(in_oklab,var(--bg-1)_70%,transparent)] px-[18px] backdrop-blur-[10px] max-md:gap-2 max-md:px-3">
      {onMenu && (
        <Button
          variant="icon"
          size="md"
          className="flex-none bg-transparent shadow-none md:hidden"
          aria-label="Open menu"
          aria-controls="v2-sidebar"
          aria-expanded={menuOpen}
          data-testid="v2-menu-button"
          onClick={onMenu}
        >
          <Menu className="h-[18px] w-[18px]" />
        </Button>
      )}
      <span className="v2-tb-title min-w-0 truncate whitespace-nowrap text-[15px] font-semibold tracking-[-0.02em] text-[var(--tx-hi)]">
        {title}
      </span>

      {/* Project context strip */}
      {selectedProject ? (
        <div className="flex max-w-[460px] max-md:hidden items-center gap-[9px] overflow-hidden whitespace-nowrap rounded-[var(--r-md)] border border-[var(--bd-subtle)] bg-[var(--bg-2)] px-[11px] py-[5px] text-[12px] text-[var(--tx-mid)] shadow-[inset_0_1px_0_var(--hi-top)]">
          <span className="h-[5px] w-[5px] flex-none rounded-full bg-[var(--ok)] shadow-[0_0_0_3px_var(--ok-bg)]" />
          <span className="overflow-hidden text-ellipsis">
            Project: <b className="font-[550] text-[var(--tx-hi)]">{selectedProject.owner_name}</b>
            {' · '}
            {selectedProject.street}, {selectedProject.city} {selectedProject.state}
          </span>
          <ProjectSwitcherMenu triggerLabel="Switch" />
        </div>
      ) : (
        <div className="flex items-center gap-[9px] whitespace-nowrap rounded-[var(--r-md)] border border-[var(--bd-subtle)] bg-[var(--bg-2)] px-[11px] py-[5px] text-[12px] text-[var(--tx-lo)] shadow-[inset_0_1px_0_var(--hi-top)] max-md:hidden [&_svg]:h-[14px] [&_svg]:w-[14px]">
          <IconBuilding />
          {/* On Supplement, an empty selection means "importing starts a NEW
              project" — label it that way instead of the generic copy. */}
          {location.pathname === '/completeness' ? 'New Project' : 'No project selected'}
          <ProjectSwitcherMenu triggerLabel="Select" />
        </div>
      )}

      <div className="flex-1" />

      {/* Global search */}
      <div
        className={cn(
          'flex h-8 w-64 items-center max-md:hidden gap-2 rounded-[var(--r-md)] border border-[var(--bd-subtle)] bg-[var(--bg-inset)] px-[10px] text-[var(--tx-lo)]',
          '[&_svg]:h-[15px] [&_svg]:w-[15px]',
        )}
      >
        <IconSearch />
        <Input
          type="search"
          placeholder="Search…"
          value={isDashboard ? searchQuery : undefined}
          onChange={
            isDashboard ? (e) => setSearchQuery(e.target.value) : undefined
          }
          aria-label="Search"
          className="h-auto min-w-0 flex-1 border-0 bg-transparent p-0 text-[12.5px] text-[var(--tx-hi)] shadow-none focus-visible:ring-0"
        />
        <Kbd>/</Kbd>
      </div>

      <AppearancePopover />

      <Button
        variant="icon"
        size="md"
        className="relative bg-transparent shadow-none"
        aria-label="Notifications"
      >
        <IconBell />
        <span className="absolute right-[7px] top-[7px] h-[6px] w-[6px] rounded-full border-[1.5px] border-[var(--bg-1)] bg-[var(--accent)]" />
      </Button>
    </header>
  );
}
